import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be changed
    functional: false,
    analytics: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem("cookieConsent");
    if (!cookieConsent) {
      // Small delay before showing banner
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(allAccepted));
    setShowBanner(false);
  };

  const acceptSelected = () => {
    const selected = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(selected));
    setShowBanner(false);
  };

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(onlyNecessary));
    setShowBanner(false);
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-border/40 overflow-hidden">
            {/* Main Banner */}
            {!showSettings ? (
              <div className="p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-medium text-foreground mb-2">
                      Cookie-Einstellungen
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                      Diese Website verwendet Cookies, um dir das beste Erlebnis zu bieten. 
                      Technisch notwendige Cookies sind für den Betrieb der Website erforderlich. 
                      Weitere Informationen findest du in unserer{" "}
                      <Link to="/datenschutz" className="text-primary hover:underline">
                        Datenschutzerklärung
                      </Link>.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={acceptAll}
                        className="btn-primary text-sm px-6 py-3"
                        data-testid="cookie-accept-all"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Alle akzeptieren
                      </Button>
                      <Button
                        onClick={rejectAll}
                        variant="outline"
                        className="rounded-full text-sm px-6 py-3"
                        data-testid="cookie-reject-all"
                      >
                        Nur notwendige
                      </Button>
                      <Button
                        onClick={() => setShowSettings(true)}
                        variant="ghost"
                        className="rounded-full text-sm px-6 py-3 text-muted-foreground"
                        data-testid="cookie-settings-btn"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Einstellungen
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Settings Panel */
              <div className="p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-xl font-medium text-foreground">
                    Cookie-Einstellungen
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="cookie-settings-close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Necessary Cookies */}
                  <div className="flex items-start gap-4 p-4 bg-sage-50 rounded-xl">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-foreground">Technisch notwendig</span>
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Erforderlich
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Diese Cookies sind für den Betrieb der Website unbedingt erforderlich 
                        und können nicht deaktiviert werden.
                      </p>
                    </div>
                  </div>

                  {/* Functional Cookies */}
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-border/40">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                      className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      data-testid="cookie-functional-checkbox"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-foreground block mb-1">Funktional</span>
                      <p className="text-sm text-muted-foreground">
                        Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung, 
                        wie z.B. die Speicherung deiner Präferenzen.
                      </p>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-border/40">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      data-testid="cookie-analytics-checkbox"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-foreground block mb-1">Analyse</span>
                      <p className="text-sm text-muted-foreground">
                        Diese Cookies helfen mir zu verstehen, wie Besucher mit der Website 
                        interagieren, um das Angebot zu verbessern.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={acceptSelected}
                    className="btn-primary text-sm px-6 py-3"
                    data-testid="cookie-save-settings"
                  >
                    Auswahl speichern
                  </Button>
                  <Button
                    onClick={acceptAll}
                    variant="outline"
                    className="rounded-full text-sm px-6 py-3"
                  >
                    Alle akzeptieren
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
